import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/shopify-schnittstellen";
const TITLE = "Shopify Schnittstellen: Warenwirtschaft, ERP und Buchhaltung anbinden";
const DESCRIPTION =
  "Shopify Schnittstellen im Überblick: Billbee, JTL, plentymarkets, DATEV und individuelle API-Anbindungen. Was Standard-Apps leisten und wann eine Custom App nötig wird.";
const UPDATED = "2026-09-12";

const ANSWER =
  "Shopify lässt sich über Apps und API an Warenwirtschaft, ERP und Buchhaltung anbinden. Für Billbee, JTL oder plentymarkets reichen meist bewährte Apps oder Konnektoren, bei DATEV übernehmen Buchhaltungs-Apps den Belegexport. Eine individuelle Schnittstelle per Custom App wird nötig, wenn Standardlösungen die eigenen Prozesse nicht abbilden: eigene Preislogik, mehrere Systeme mit Abgleich oder automatisierte Abläufe nach Bestellung.";

const faqs = [
  {
    q: "Welche Warenwirtschaft lässt sich mit Shopify verbinden?",
    a: "Praktisch alle verbreiteten Systeme: Billbee, JTL-Wawi, plentymarkets, weclapp und andere bieten fertige Apps oder Konnektoren. Bestellungen, Bestände und Tracking-Nummern laufen automatisch in beide Richtungen.",
  },
  {
    q: "Was kostet die Anbindung einer Warenwirtschaft an Shopify?",
    a: "Apps und Konnektoren liegen meist zwischen 20 und 150 € im Monat, je nach Bestellvolumen und Funktionsumfang. Die Einrichtung mit Feldzuordnung und Testlauf dauert erfahrungsgemäß einige Stunden bis wenige Tage.",
  },
  {
    q: "Wann reicht eine Standard-App nicht mehr?",
    a: "Wenn Ihre Prozesse von der Norm abweichen: eigene Preislogik pro Kundengruppe, Stücklisten, die das System nicht versteht, mehrere Shops mit gemeinsamem Bestand oder Abläufe, die nach einer Bestellung automatisch in anderen Systemen starten sollen.",
  },
  {
    q: "Was kostet eine individuelle Shopify-Schnittstelle?",
    a: "Eine Custom App mit einer klaren Aufgabe liegt häufig bei 3.000 bis 8.000 € netto, komplexere Abgleich-Projekte darüber. Abgerechnet wird nach Aufwand zu 110 € netto pro Stunde, nach Sichtung gibt es einen festen Rahmen.",
  },
  {
    q: "Wer pflegt die Schnittstelle nach dem Livegang?",
    a: "Shopify und die Gegensysteme aktualisieren ihre APIs regelmäßig. Standard-Apps werden vom Anbieter gepflegt, individuelle Schnittstellen brauchen gelegentliche Wartung, die ich nach Aufwand ohne Vertragsbindung übernehme.",
  },
];

export const Route = createFileRoute("/ratgeber/shopify-schnittstellen")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      faqs,
      section: "Shopify Schnittstellen",
      updated: UPDATED,
    }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Schnittstellen"
      title="Shopify Schnittstellen: wenn der Shop mit anderen Systemen sprechen muss"
      intro="Bestellungen in die Warenwirtschaft, Belege in die Buchhaltung, Bestände in beide Richtungen: Dieser Überblick zeigt, welche Anbindungen mit Standard-Apps laufen und wo individuelle Entwicklung beginnt."
      source="ratgeber-shopify-schnittstellen"
      answer={ANSWER}
      updated={UPDATED}
      sections={[
        {
          h: "Die drei klassischen Anbindungen",
          p: [
            "Fast jedes Schnittstellenprojekt fällt in eines von drei Mustern. Die Einordnung entscheidet über Weg und Budget.",
          ],
          list: [
            "Warenwirtschaft und Fulfillment: Billbee, JTL, plentymarkets oder weclapp für Bestellungen, Bestände und Sendungsnummern",
            "Buchhaltung: Belegexport Richtung DATEV über Buchhaltungs-Apps, sauber eingerichtet mit Gutschriften und Gebühren",
            "ERP und PIM: Produktdaten, Preise und Kundengruppen aus dem führenden System in Shopify synchronisieren",
          ],
        },
        {
          h: "Standard-App oder eigene Entwicklung",
          p: [
            "Standard-Apps sind der richtige Start: schnell eingerichtet, vom Anbieter gepflegt, monatlich kalkulierbar. Sie scheitern dort, wo Ihr Prozess von der Norm abweicht.",
            "Eine Custom App bildet genau Ihre Logik ab: eigene Preisregeln, Abgleich mehrerer Systeme, automatisierte Folgeprozesse nach Bestellung. Sie läuft nur in Ihrem Shop, ohne App-Store-Review, mit Zugriffsrechten auf das Nötige begrenzt.",
          ],
        },
        {
          h: "Billbee, JTL und die deutschen Besonderheiten",
          p: [
            "Billbee ist der schnelle Einstieg für kleinere Shops, JTL-Wawi der Standard für gewachsene Strukturen mit Stücklisten und eigenen Workflows. Beide lassen sich mit Shopify verbinden, die Qualität der Abbildung hängt an der Einrichtung: Steuersätze, Versandprofile, Bundles.",
            "Deutsche Besonderheiten wie Rechnungskauf, Gutschriften und gemischte Steuersätze gehören in den Testlauf, nicht in die Nachbesserung nach dem ersten Mahnlauf.",
          ],
        },
        {
          h: "Was ein Schnittstellenprojekt belastbar macht",
          p: [
            "Drei Dinge entscheiden über Stabilität: ein führendes System pro Datenart, ein definierter Umgang mit Fehlern statt stillem Ignorieren und ein Testlauf mit echten Bestellungen vor dem scharfen Betrieb.",
            "Genau daran messe ich Angebote mit: Wer keinen Fehlerfall und keinen Testlauf benennt, verkauft die halbe Schnittstelle.",
          ],
        },
        {
          h: "Ablauf und Kostenrahmen",
          p: [
            "Nach einem kurzen Gespräch sichte ich Systeme und Datenwege und liefere eine Aufwandsschätzung mit fester Obergrenze. Einrichtung bestehender Apps zu 110 € netto pro Stunde, individuelle Schnittstellen meist zwischen 3.000 und 8.000 € netto, komplexe Abgleiche darüber.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/ratgeber/shopify-datev-schnittstelle", label: "DATEV anbinden" },
        { to: "/shopify-app-entwicklung", label: "Individuelle Schnittstelle bauen lassen" },
        { to: "/shopify-entwickler", label: "Shopify Entwickler beauftragen" },
        { to: "/ratgeber/jtl-zu-shopify", label: "JTL-Wawi mit Shopify" },
        { to: "/kontakt", label: "Schnittstelle anfragen" },
      ]}
    />
  );
}
